import { NavLink } from 'react-router-dom';
import { Users, Building2, UserCheck, LogOut, Settings, Handshake, BookOpen, X, Euro, Target, Bot, CheckSquare, UserPlus, Calendar, ChevronDown, ChevronRight, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { logout } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';

const navigationSections = [
  {
    title: 'CAMPAGNES',
    items: [
      { name: 'Collaborations', path: '/app/collaborations', icon: Handshake },
      { name: 'Casting', path: '/app/casting', icon: Target },
    ]
  },
  {
    title: 'TÂCHES & PLANNING',
    items: [
      { name: 'Mes tâches', path: '/app/todo', icon: CheckSquare },
      { name: 'Tâches influenceurs', path: '/app/daily-brief', icon: Calendar },
    ]
  },
  {
    title: 'CONTACTS',
    items: [
      { name: 'Clients', path: '/app/clients', icon: Building2 },
      { name: 'Influenceurs', path: '/app/influencers', icon: UserCheck },
      { name: 'Recrutement', path: '/app/recruitment', icon: UserPlus },
    ]
  },
  {
    title: 'OUTILS',
    items: [
      { name: 'Comptabilité', path: '/app/accounting', icon: Euro },
      { name: 'Ressources', path: '/app/resources', icon: BookOpen },
      { name: 'Assistant IA', path: '/app/assistant', icon: Bot, superAdminOnly: true },
      { name: 'Approbation utilisateurs', path: '/superadmin/user-approval', icon: UserCheck, superAdminOnly: true },
    ]
  },
  {
    title: 'Paramètres',
    items: [
      { name: 'Mon compte', path: '/app/account', icon: Settings },
    ]
  }
];

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Gestion des campagnes': true,
    'Tâches & Planning': true,
    'Contacts & Recrutement': true,
    'Gestion & Outils': false,
    'Paramètres': false,
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const isSuperAdmin = currentUser?.email === 'hosni@swipe-upp.com';

  // Styles pour les sections
  const sectionTitleClass = theme === 'dark' 
    ? 'text-gray-500 hover:text-gray-400' 
    : 'text-gray-500 hover:text-gray-700';

  // Styles pour les items de navigation
  const navItemClass = ({ isActive }: { isActive: boolean }) => cn(
    'flex items-center px-3 sm:px-4 py-2 text-sm rounded-lg transition-colors',
    theme === 'dark'
      ? `text-gray-400 hover:bg-dark-200 ${isActive && 'bg-dark-200/80 text-white border-l-2 border-accent-blue'}`
      : `text-gray-600 hover:bg-gray-100 ${isActive && 'bg-gray-100 text-gray-900 border-l-2 border-accent-blue'}`
  );

  return (
    <div className="flex flex-col h-full">
      <div className={`h-14 sm:h-16 flex items-center px-4 sm:px-6 border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2 flex-1">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <h1 className={`text-lg sm:text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Le Suivi
          </h1>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`p-1.5 sm:p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-dark-200' : 'hover:bg-gray-100'} lg:hidden`}
          >
            <X className={`w-4 h-4 sm:w-5 sm:h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        )}
      </div>
      
      <nav className="flex-1 px-2 sm:px-3 py-4 sm:py-5 overflow-y-auto">
        {navigationSections.map((section) => {
          const filteredItems = section.items.filter(item => 
            !item.superAdminOnly || isSuperAdmin
          );
          
          if (filteredItems.length === 0) return null;
          
          const isExpanded = expandedSections[section.title];
          
          return (
            <div key={section.title} className="mb-6">
              <button
                onClick={() => toggleSection(section.title)}
                className={`flex items-center justify-between w-full px-2 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${sectionTitleClass} mb-1`}
              >
                <span>{section.title}</span>
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 opacity-70" />
                )}
              </button>
              
              {isExpanded && (
                <div className={`mt-1 space-y-0.5 pl-1 ${theme === 'dark' ? 'bg-dark-200/20' : 'bg-gray-50'} rounded-lg py-1.5`}>
                  {filteredItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={navItemClass}
                      >
                        <Icon className="w-4 h-4 mr-2.5" />
                        {item.name}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        
        <div className="mt-8 pt-4 border-t border-dark-200">
          <button
            onClick={handleLogout}
            className={`flex items-center w-full px-3 sm:px-4 py-2 text-sm rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-400 hover:bg-dark-200'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <LogOut className="w-4 h-4 mr-2.5" />
            Déconnexion
          </button>
        </div>
      </nav>
    </div>
  );
}